/**
 * Web-push subscription glue.
 *
 * The server provides `/api/me/push/config` with the VAPID public key and
 * an `enabled` flag (false if the server's WEB_PUSH_VAPID_* envs are
 * unset — see docs/DEPLOY-BLOCKERS §3). The browser handles the rest via
 * the standard Push API on top of a service worker registered at /sw.js.
 *
 * This module wraps the dance so UI code can stay declarative:
 *   - getPushPermission()       — current notification permission
 *   - getServerPushStatus()     — { enabled, publicKey } from the server
 *   - getActiveSubscription()   — local PushSubscription if any
 *   - enableBrowserPush()       — request permission + subscribe + POST
 *   - disableBrowserPush()      — unsubscribe locally + DELETE on server
 *
 * Browsers without the Push API (Safari < 16.4 on macOS, all of iOS PWA
 * pre-16.4) just see `supportsPush() === false` and the UI hides the
 * toggle rather than tease a feature that can't fire.
 */

import { api } from '@/lib/api';
import i18n from '@/i18n';

const SW_URL = '/sw.js';

export function supportsPush(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPushPermission(): NotificationPermission {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
}

export interface ServerPushStatus {
  enabled: boolean;
  publicKey: string | null;
}

/** What the server thinks: VAPID configured, public key for subscribe. */
export async function getServerPushStatus(): Promise<ServerPushStatus> {
  return await api.request_pushConfig();
}

/** Register the SW (idempotent — the browser dedupes by scope). */
async function ensureRegistration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.register(SW_URL);
  // Wait for the SW to become active. Without this, getSubscription()
  // can return null on first load even though we're about to be ready.
  if (reg.installing) {
    await new Promise<void>((resolve) => {
      reg.installing!.addEventListener('statechange', () => {
        if (reg.active) resolve();
      });
    });
  }
  return reg;
}

/** Returns the existing PushSubscription for this browser, if any. */
export async function getActiveSubscription(): Promise<PushSubscription | null> {
  if (!supportsPush()) return null;
  try {
    const reg = await ensureRegistration();
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Full enable path: ask the browser for permission, subscribe to the
 * push manager with the server's VAPID public key, send the subscription
 * to our server.
 *
 * Throws an Error with a human-readable Russian message on every failure
 * branch so the UI can just toast.error(err.message).
 */
export async function enableBrowserPush(): Promise<PushSubscription> {
  if (!supportsPush()) {
    throw new Error(i18n.t('lib2.push.unsupported'));
  }
  const status = await getServerPushStatus();
  if (!status.enabled || !status.publicKey) {
    throw new Error(i18n.t('lib2.push.serverNotConfigured'));
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    throw new Error(
      perm === 'denied'
        ? i18n.t('lib2.push.denied')
        : i18n.t('lib2.push.notGranted'),
    );
  }
  const reg = await ensureRegistration();
  const existing = await reg.pushManager.getSubscription();
  // If something is already subscribed for a different VAPID key (server
  // rotated keys, or a stale dev subscription), drop it before re-subbing.
  if (existing) {
    await existing.unsubscribe().catch(() => undefined);
  }
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    // Cast to BufferSource — Uint8Array's `buffer` is `ArrayBufferLike` in
    // recent lib.dom (which includes SharedArrayBuffer), but the PushManager
    // really accepts a regular ArrayBuffer view here.
    applicationServerKey: urlBase64ToUint8Array(status.publicKey) as BufferSource,
  });
  await api.subscribePush(subscriptionBody(sub));
  return sub;
}

function subscriptionBody(sub: PushSubscription) {
  // toJSON() returns a flat dict with the two key fields — TS doesn't know
  // that, hence the cast.
  const keys = (sub.toJSON() as { keys?: { p256dh?: string; auth?: string } }).keys;
  return { endpoint: sub.endpoint, keys: { p256dh: keys?.p256dh ?? '', auth: keys?.auth ?? '' } };
}

/**
 * Register this browser's existing subscription with the server again. The
 * server binds a subscription to the sign-in that registered it and deletes it
 * when that session ends, so after a new sign-in (or for a subscription made
 * before sessions were recorded) it has to be re-sent. Never asks for
 * permission and never creates a subscription: without `granted` permission,
 * a service worker or a subscription it does nothing. Resolves to whether the
 * server now holds this browser's subscription.
 */
export async function resyncBrowserPush(): Promise<boolean> {
  if (!supportsPush() || getPushPermission() !== 'granted') return false;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return false;
  await api.subscribePush(subscriptionBody(sub));
  return true;
}

let resyncedFor: string | null = null;

/** {@link resyncBrowserPush} once per signed-in user per page load (app start
 *  and the sign-in that follows a sign-out both land here). Failures are only
 *  logged: a browser without a fresh registration just gets no pushes. */
export function resyncBrowserPushOnce(userId: string): void {
  if (resyncedFor === userId) return;
  resyncedFor = userId;
  resyncBrowserPush().catch((err: unknown) => {
    resyncedFor = null;
    console.warn('web-push: could not re-register this browser', err);
  });
}

/** Unsubscribe + tell the server to forget the endpoint. Idempotent. */
export async function disableBrowserPush(): Promise<void> {
  if (!supportsPush()) return;
  const reg = await ensureRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => undefined);
  await api.unsubscribePush(endpoint).catch(() => undefined);
}

/**
 * VAPID public keys are sent as URL-safe base64 strings; the PushManager
 * wants raw bytes. This is the canonical conversion the web-push docs
 * ship — no library, six lines.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}
